// Sash — the hand-rolled resize handle between two split panes, pure/props.
//
// ui-affordances-resize-reorder-tooltip.md: a `role="separator"` divider with a
// keyboard path (arrow keys), an `aria-valuenow`, and a double-click reset.
// PROPORTIONS, not pixels: the shell stores fractions in the pane tree; this
// handle reports a DELTA fraction and the shell folds `resize-divider`. The
// pointer math (px→fraction against the branch container) is the impure shell
// bit and lives here in the handle's own pointer handlers; the decision (clamp,
// re-normalise) is the pure `resizeDivider` fold.
//
// Orientation: a ROW branch (children side-by-side) has a VERTICAL sash the
// user drags horizontally; a COLUMN branch has a HORIZONTAL sash dragged
// vertically. `aria-orientation` is the sash's own axis (perpendicular to the
// branch axis).

import { useLingui } from "@lingui/react/macro";
import { useRef } from "react";
import type { Orientation } from "../Navigation/paneTree.js";
import "./Sash.css";

// One arrow-key press shifts this fraction of the branch between neighbours.
const KEY_STEP = 0.02;

export type SashProps = {
    // The owning branch's orientation (row = children side-by-side).
    readonly orientation: Orientation;
    // The preceding child's current proportion (0–1) — drives aria-valuenow.
    readonly fraction: number;
    // Shift `delta` (a proportion) from the following child to the preceding
    // one (positive) or back (negative). The shell dispatches `resize-divider`.
    readonly onResizeBy: (delta: number) => void;
    // Reset the two neighbours to an equal split (double-click / Home).
    readonly onReset: () => void;
    readonly className?: string;
};

export const Sash = ({
    orientation,
    fraction,
    onResizeBy,
    onReset,
    className,
}: SashProps) => {
    const { t } = useLingui();
    const ref = useRef<HTMLDivElement | null>(null);
    const last = useRef<number | null>(null);

    // The branch container extent along the drag axis — the px→fraction
    // denominator. Measured from the sash's parent (the flex branch).
    const axisSize = (): number => {
        const parent = ref.current?.parentElement;
        if (parent === null || parent === undefined) return 0;
        return orientation === "row" ? parent.clientWidth : parent.clientHeight;
    };

    const posOf = (e: React.PointerEvent): number =>
        orientation === "row" ? e.clientX : e.clientY;

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        last.current = posOf(e);
    };
    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (last.current === null) return;
        const size = axisSize();
        if (size <= 0) return;
        const deltaPx = posOf(e) - last.current;
        last.current = posOf(e);
        onResizeBy(deltaPx / size);
    };
    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (last.current === null) return;
        last.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // F81 — never shadow a browser chord: a MODIFIED arrow (e.g. Cmd+→ =
        // browser forward-nav) must fall through to the browser. Only bare
        // arrows/Home drive the resize.
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        // Arrow toward the following child shrinks the preceding one, and back.
        const grow =
            e.key === (orientation === "row" ? "ArrowRight" : "ArrowDown");
        const shrink =
            e.key === (orientation === "row" ? "ArrowLeft" : "ArrowUp");
        if (grow || shrink) {
            e.preventDefault();
            onResizeBy(grow ? KEY_STEP : -KEY_STEP);
        } else if (e.key === "Home") {
            e.preventDefault();
            onReset();
        }
    };

    return (
        // biome-ignore lint/a11y/useSemanticElements: role="separator" with aria-valuenow IS the correct ARIA for a resize handle; there is no native element for it.
        <div
            ref={ref}
            role="separator"
            aria-label={t`Resize panes`}
            aria-orientation={orientation === "row" ? "vertical" : "horizontal"}
            aria-valuenow={Math.round(fraction * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            className={`bilda-sash bilda-sash--${orientation}${
                className ? ` ${className}` : ""
            }`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onDoubleClick={onReset}
            onKeyDown={onKeyDown}
        />
    );
};
